import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiFetch, ApiError } from "../lib/api";

/** @typedef {{ username: string }} AdminUser */

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [user, setUser] = useState(/** @type {AdminUser | null} */ (null));
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const body = await apiFetch("/admin/auth/me");
      setUser(body?.data?.user ?? null);
      return body?.data?.user ?? null;
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setUser(null);
        return null;
      }
      throw err;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        await refreshMe();
      } catch {
        if (!cancelled) {
          setUser(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [refreshMe]);

  const login = useCallback(async (username, password) => {
    const body = await apiFetch("/admin/auth/login", {
      method: "POST",
      body: JSON.stringify({ username, password }),
    });
    setUser(body?.data?.user ?? null);
    return body?.data?.user ?? null;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch("/admin/auth/logout", { method: "POST" });
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, logout, refreshMe }),
    [user, loading, login, logout, refreshMe]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) {
    throw new Error("useAdminAuth must be used within AdminAuthProvider");
  }
  return ctx;
}
